<template>
  <div class="chat-write-box">
    <div class="chat-write-box__privacy-select">
      <div
        class="chat-write-box__privacy-select__all"
        @click="changeChannel(false)"
      >
        <div
          v-if="unreadMessages"
          class="chat-write-box__privacy-select__all__alert"
        >
          {{ unreadMessages }}
        </div>
        <div>Todos</div>
      </div>
      <div
        class="chat-write-box__privacy-select__backstage"
        @click="changeChannel(true)"
      >
        <div
          v-if="privateUnreadMessages"
          class="chat-write-box__privacy-select__backstage__alert"
        >
          {{ privateUnreadMessages }}
        </div>
        <div>Bastidores</div>
      </div>
    </div>
    <RichText
      id="channel-message"
      ref="editor"
      data-testid="channel-message"
      :value="draftMessage"
      :update-value="sentToChannels"
      class="chat-write-box__text"
      :disabled="disableRichText"
      :placeholder="placeholder"
      :actions="{ file: true, bold: true, italic: true }"
      :enter-break-line="false"
      :auto-focus="!disableRichText"
      :transform-option="transformOption"
      :filter-options="findMacro"
      :mention-list="mentionList"
      data-dd-action-name="channel_message_writebox"
      :focus="updateFocus"
      @press-enter="pressEnter"
      @attachment-function="fileUpload"
      @typing="syncTyping"
    >
      <CReplyBox
        v-if="replyTo"
        :message="replyTo"
        :sender="replyMessageSender"
      />
    </RichText>
    <div v-if="isSendingFile" class="chat-write-box__loading-file">
      Enviando arquivo...
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

import { RichText } from '@/components/shared/atoms'
import { ChannelStatus } from '@/models/channels'
import Logger, { EventModel, EventName } from '@/wrappers/logger'

import CReplyBox from './CReplyBox.vue'

export default {
  name: 'CWriteBox',
  components: { RichText, CReplyBox },
  inject: ['notify'],
  props: {
    channel: { type: Object, required: true },
    isPrivate: {
      type: Boolean
    },
    disabled: { type: Boolean }
  },
  emits: ['isPrivate', 'messageSent'],
  data() {
    return {
      message: '',
      typing: false
    }
  },
  computed: {
    ...mapGetters('user', ['user']),
    ...mapState('pillars/channel/message', [
      'macros',
      'usedMacros',
      'isSendingFile',
      'replyTo'
    ]),
    ...mapGetters('member', ['getMemberById']),
    ...mapGetters('pillars/channel/message', [
      'getDraftMessage',
      'getMessageSender'
    ]),
    ...mapGetters('bud', ['sentToChannels']),
    mentionList() {
      return this.isPrivate ? this.channel.staffArray : []
    },
    placeholder() {
      if (this.channelIsArchived) {
        const date = this.channel.archivedAt
          ?.toDate()
          ?.toLocaleString('pt-BR', { dateStyle: 'short' })
        return date ? `Caso encerrado em: ${date}` : 'Caso encerrado'
      } else if (this.channelIsMerged) {
        return 'Esse canal já foi mesclado'
      } else if (this.disabled) {
        return 'Envio de mensagens não disponível'
      } else if (this.isPrivate) {
        return 'Escreva para o time de saúde'
      }
      return `Escreva sua mensagem para ${this.person?.displayName}`
    },
    channelIsArchived() {
      return this.channel.status === ChannelStatus.ARCHIVED
    },
    channelIsMerged() {
      return this.channel.status === ChannelStatus.MERGED
    },
    disableRichText() {
      return this.channelIsMerged || this.channelIsArchived || this.disabled
    },
    person() {
      return this.getMemberById({
        channelPersonId: this.channel.channelPersonId
      })
    },
    staffNotifications() {
      return this.channel.staff[this.user.id]
    },
    unreadMessages() {
      return this.staffNotifications?.unreadMessages
    },
    privateUnreadMessages() {
      return this.staffNotifications?.privateUnreadMessages
    },
    replyMessageSender() {
      return this.getMessageSender(this.replyTo)
    },
    updateFocus() {
      return this.replyTo ? true : false
    },
    draftMessage() {
      return this.getDraftMessage(this.channel.id, this.isPrivate)?.message
    }
  },
  watch: {
    typing() {
      this.$store.dispatch('pillars/channel/message/updateTyping', {
        typing: this.typing,
        channel: this.channel
      })
    },
    draftMessage(value) {
      if (value) this.typing = true
    }
  },
  created() {
    window.addEventListener('keydown', this.handleEsc)
  },
  beforeUnmount() {
    if (this.typing) {
      this.updateVisualization()
    }
  },
  unmounted() {
    window.removeEventListener('keydown', this.handleEsc)
  },
  methods: {
    fileUpload(file) {
      this.$store.dispatch('pillars/channel/message/sendFile', {
        file,
        channel: this.channel
      })
    },
    pressEnter({ message, mentions: mentionStaffIds }) {
      this.$store.commit('pillars/channel/message/removeDraftMessage', {
        channelId: this.channel.id,
        isPrivate: this.isPrivate
      })

      this.sendMessage({
        message: message.replaceAll(
          /"javascript:pbxfun\D+(\d+).*?"/g,
          'tel:$1'
        ),
        mentionStaffIds
      })
    },
    changeChannel(value) {
      this.$emit('isPrivate', value)
      if (value & this.channel.tags.includes('triagem_ia')) {
        Logger.addLog(EventName.USER_INTERACTION, {
          model: EventModel.CHANNELS,
          modelId: this.channel.channelId,
          data: {
            context: 'clickedOnBackstageChannel',
            value: this.channel.tags
          }
        })
      }
    },
    async sendMessage({ message, type, mentionStaffIds = [] }) {
      if (this.messageIsEmpty(message)) {
        return
      }

      await this.$store.dispatch('pillars/channel/message/sendMessage', {
        message,
        type,
        mentionStaffIds,
        channel: this.channel
      })

      this.message = ''

      const updateLastSync = !this.isPrivate
      this.updateVisualization(updateLastSync)

      this.$emit('messageSent')
    },
    updateVisualization(withLastSync) {
      this.typing = false
      if (this.$refs.editor) this.$refs.editor.reset()
      this.$store.dispatch('pillars/channel/message/updateChannel', {
        withLastSync,
        channel: this.channel
      })
    },
    syncTyping(message) {
      this.$store.commit('pillars/channel/message/handleDraftMessage', {
        channelId: this.channel.id,
        isPrivate: this.isPrivate,
        message
      })

      this.typing = !this.messageIsEmpty(message)
    },
    messageIsEmpty(content) {
      if (content === undefined) return true

      const template = document.createElement('template')
      template.innerHTML = content.trim()

      const pTag = template.content.firstChild

      return !pTag || pTag.innerHTML === ''
    },
    handleEsc(event) {
      if (event.key === 'Escape' || event.keyCode === 27) {
        this.updateVisualization(true)
      }
    },
    transformOption(option) {
      this.$store.commit('pillars/channel/message/setUsedMacro', option)

      const member = this.person
      const value = option.value
        .replace(/@memberName/g, member?.displayName)
        .replace(/@memberFullName/g, member?.sanitizedFullName)
        .replace(/@memberEmail/g, member?.email)
        .replace(/@staffName/g, this.user.firstName)
      return { ...option, value }
    },
    async findMacro(query) {
      if (!query || query.length < 2) return this.usedMacros

      await this.$store.dispatch('pillars/channel/message/fetchMacroByQuery', {
        query
      })
      return this.macros
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/style/_non-rendering';
.chat-write-box {
  width: 100%;
  position: relative;
  background-color: var(
    --background-color,
    var(--sys-color-surface-background-light)
  );
  padding: var(--gl-spacing-04);

  &__privacy-select {
    display: flex;
    justify-content: space-between;
    height: 40px;
    border-radius: 100px;
    background-color: var(
      --buttons-background-color,
      var(--gl-color-shades-gray-20)
    );
    padding: 4px;

    .button {
      align-items: center;
      border-radius: 100px;
      width: 50%;
      height: 32px;
      cursor: pointer;
      display: flex;
      justify-content: center;
    }

    .alert {
      @extend %caption;
      align-self: center;
      border-radius: 20px;
      text-align: center;
      padding: 0 6px;
      background: var(--sys-color-surface-background-default);
      min-width: 24px;
      margin-right: 12px;
    }

    &__all {
      @extend .button;
      background-color: var(
        --buttons-background-color,
        var(--gl-color-shades-white)
      );
      color: var(--default-contrast-color, var(--sys-color-content-active));

      &__alert {
        @extend .alert;
        background-color: var(--sys-color-surface-background-default);
        color: var(--sys-color-content-active);
      }
    }

    &__backstage {
      @extend .button;
      background-color: var(--button-background-active, transparent);
      color: var(
        --default-contrast-brand-color,
        var(--sys-color-content-primary)
      );

      &__alert {
        @extend .alert;
        background-color: var(--gl-color-shades-gray-60);
      }
    }
  }

  &__text {
    border-radius: 10px;
    margin: var(--gl-spacing-02) 0;
    box-sizing: border-box;

    &[disabled='true'] {
      background-color: var(
        --background-disabled-color,
        var(--gl-color-shades-gray-10)
      );
    }
  }

  &__loading-file {
    color: var(--sys-color-content-placeholder);
    font-size: var(--gl-font-size-xxxs);
    position: absolute;
    bottom: 0;
  }
}
</style>
